import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { toast } from "sonner";
import { Send, Hash, MessageSquare, Trash2, Flag, Ban } from "lucide-react";

const MESSAGE_POLL_INTERVAL = 8000; // ms — how often Comms refreshes messages/unread

function Bubble({ m, mine, canDelete, onDelete, onReport, onBlock }) {
  const del = canDelete && (
    <button data-testid={`msg-del-${m.id}`} onClick={onDelete}
      className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#FF003C] transition-all duration-200 shrink-0">
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
  const mod = !mine && (
    <div className="flex items-center gap-1.5 shrink-0">
      <button data-testid={`msg-report-${m.id}`} onClick={onReport} title="Report message"
        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#FFB800] transition-all duration-200">
        <Flag className="w-3.5 h-3.5" />
      </button>
      <button data-testid={`msg-block-${m.id}`} onClick={onBlock} title="Block user"
        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#FF003C] transition-all duration-200">
        <Ban className="w-3.5 h-3.5" />
      </button>
    </div>
  );
  return (
    <div className={`group flex items-center gap-2 ${mine ? "justify-end" : "justify-start"}`}>
      {mine && del}
      <div className={`max-w-[75%] px-3 py-2 border ${mine
        ? "bg-[#8A2BE2]/15 border-[#B15CFF]/40 text-white"
        : "bg-[#05060A] border-slate-800 text-slate-200"}`}>
        {!mine && <div className="text-[10px] font-mono-rs uppercase tracking-wider text-[#00F0FF] mb-0.5">{m.sender_name}</div>}
        <div className="text-sm break-words">{m.text}</div>
        <div className="text-[9px] text-slate-500 mt-1 text-right">
          {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      {!mine && (canDelete ? <div className="flex items-center gap-1.5 shrink-0">{del}{mod}</div> : mod)}
    </div>
  );
}

export default function Comms() {
  const { user } = useAuth();
  const [tab, setTab] = useState("general");

  // general
  const [general, setGeneral] = useState([]);
  const [gText, setGText] = useState("");
  // dm
  const [contacts, setContacts] = useState([]);
  const [unread, setUnread] = useState({});
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [dText, setDText] = useState("");
  const endRef = useRef(null);

  const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

  const loadGeneral = () => api.get("/comms/general").then((r) => setGeneral(r.data)).catch((e) => console.error("Failed to load general messages", e));
  const loadContacts = () => api.get("/comms/contacts").then((r) => setContacts(r.data)).catch((e) => console.error("Failed to load contacts", e));
  const loadUnread = () => api.get("/comms/unread").then((r) => setUnread(r.data.per_user || {})).catch((e) => console.error("Failed to load unread counts", e));
  const loadThread = (uid) => api.get(`/comms/dm?with_user=${uid}`).then((r) => { setThread(r.data); scrollDown(); loadUnread(); }).catch((e) => console.error("Failed to load thread", e));

  useEffect(() => { loadGeneral(); loadContacts(); loadUnread(); }, []);
  useEffect(() => {
    const t = setInterval(() => {
      if (tab === "general") loadGeneral();
      else { loadUnread(); if (active) loadThread(active.id); }
    }, MESSAGE_POLL_INTERVAL);
    return () => clearInterval(t);
  }, [tab, active]);
  useEffect(() => { if (tab === "general") scrollDown(); }, [general, tab]);

  const sendGeneral = async () => {
    const t = gText.trim();
    if (!t) return;
    setGText("");
    try { await api.post("/comms/general", { text: t }); loadGeneral(); scrollDown(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openContact = (c) => { setActive(c); loadThread(c.id); };
  const deleteMsg = async (id, kind) => {
    try {
      await api.delete(`/comms/message/${id}`);
      if (kind === "general") loadGeneral();
      else if (active) loadThread(active.id);
      toast.success("Message deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendDM = async () => {
    const t = dText.trim();
    if (!t || !active) return;
    setDText("");
    try { await api.post("/comms/dm", { recipient_id: active.id, text: t }); loadThread(active.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportMsg = async (m) => {
    try {
      await api.post("/comms/report", { reported_user_id: m.sender_id, message_id: m.id, reason: "Reported from chat" });
      toast.success("Message reported. Our team reviews reports within 24 hours.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const blockUser = async (m) => {
    if (!window.confirm(`Block ${m.sender_name}? You will no longer see their messages and they cannot message you.`)) return;
    try {
      await api.post(`/comms/block/${m.sender_id}`);
      toast.success(`${m.sender_name} blocked`);
      loadGeneral(); loadContacts();
      if (active?.id === m.sender_id) setActive(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabCls = (t) => `flex items-center gap-2 px-4 py-2 text-sm font-mono-rs uppercase tracking-wider border-b-2 transition-all duration-300 ${
    tab === t ? "border-[#D902EE] text-white" : "border-transparent text-slate-400 hover:text-white"}`;

  const totalUnread = useMemo(() => Object.values(unread).reduce((a, b) => a + b, 0), [unread]);

  return (
    <div>
      <PageHeader title="Comms" subtitle="// Department chat & direct messages" />

      <div className="flex border-b border-slate-800 mb-6">
        <button data-testid="comms-tab-general" onClick={() => setTab("general")} className={tabCls("general")}>
          <Hash className="w-4 h-4" /> General
        </button>
        <button data-testid="comms-tab-direct" onClick={() => setTab("direct")} className={tabCls("direct")}>
          <MessageSquare className="w-4 h-4" /> Direct {totalUnread > 0 && <span className="text-[#D902EE]">({totalUnread})</span>}
        </button>
      </div>

      {tab === "general" && (
        <div className="bg-[#0F1219] border border-slate-800 flex flex-col h-[65vh]">
          <div data-testid="general-feed" className="flex-1 overflow-y-auto p-4 space-y-3">
            {general.length === 0 && <p className="text-slate-500 text-sm text-center pt-6">No messages yet. Say hello to the department.</p>}
            {general.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id}
              canDelete={m.sender_id === user.id || user.role === "admin"} onDelete={() => deleteMsg(m.id, "general")}
              onReport={() => reportMsg(m)} onBlock={() => blockUser(m)} />)}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-800 p-3 flex gap-2">
            <input data-testid="general-input" value={gText} onChange={(e) => setGText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendGeneral()} placeholder="Message everyone…"
              className="flex-1 bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors" />
            <button data-testid="general-send" onClick={sendGeneral}
              className="px-4 border border-[#D902EE] bg-[#D902EE]/10 text-white hover:bg-[#D902EE]/25 transition-all"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {tab === "direct" && (
        <div className="grid md:grid-cols-[240px_1fr] gap-4">
          <div className="bg-[#0F1219] border border-slate-800 h-[65vh] overflow-y-auto" data-testid="dm-contacts">
            {contacts.map((c) => (
              <button key={c.id} data-testid={`dm-contact-${c.id}`} onClick={() => openContact(c)}
                className={`w-full text-left px-3 py-3 border-b border-slate-800/60 flex items-center justify-between hover:bg-[#171A21] transition-colors ${
                  active?.id === c.id ? "bg-[#8A2BE2]/10 border-l-2 border-l-[#B15CFF]" : ""}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{c.name}</div>
                  <div className="text-[10px] font-mono-rs uppercase tracking-wider text-slate-500">{c.role === "admin" ? "Admin" : c.category}</div>
                </div>
                {unread[c.id] > 0 && <span className="min-w-[18px] h-[18px] px-1 text-[10px] bg-[#D902EE] text-white rounded-full flex items-center justify-center">{unread[c.id]}</span>}
              </button>
            ))}
            {contacts.length === 0 && <p className="text-slate-500 text-sm p-4">No contacts.</p>}
          </div>

          <div className="bg-[#0F1219] border border-slate-800 flex flex-col h-[65vh]">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Select someone to start chatting.</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-800 font-mono-rs text-sm text-white">{active.name}</div>
                <div data-testid="dm-thread" className="flex-1 overflow-y-auto p-4 space-y-3">
                  {thread.length === 0 && <p className="text-slate-500 text-sm text-center pt-6">No messages yet.</p>}
                  {thread.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id}
                    canDelete={m.sender_id === user.id || user.role === "admin"} onDelete={() => deleteMsg(m.id, "dm")}
                    onReport={() => reportMsg(m)} onBlock={() => blockUser(m)} />)}
                  <div ref={endRef} />
                </div>
                <div className="border-t border-slate-800 p-3 flex gap-2">
                  <input data-testid="dm-input" value={dText} onChange={(e) => setDText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendDM()} placeholder={`Message ${active.name}…`}
                    className="flex-1 bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors" />
                  <button data-testid="dm-send" onClick={sendDM}
                    className="px-4 border border-[#D902EE] bg-[#D902EE]/10 text-white hover:bg-[#D902EE]/25 transition-all"><Send className="w-4 h-4" /></button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
